import { BUILDING_SUPPORT_RESOURCES_BY_SKILL, RESOURCE_BY_SKILL, SKILLS, type SkillId } from './catalog.ts';
import type {
  ContractCategory, ContractMetric, ContractObjective, GameState, RealmActivityState, RealmContract, RealmRift, RewardKind, RewardToken
} from './types.ts';
import {
  ARTIFACTS, ARTIFACT_RARITY_WEIGHTS, artifactDuplicateDust, artifactDustCost, artifactRarity
} from './artifacts.ts';
import { constellationSurgeDurationMultiplier } from './constellation.ts';

export const CONTRACT_SLOTS = 3;
export const PRODUCTION_SURGE_MULTIPLIER = 1.5;
export const RIFT_LIFETIME_MS = 30_000;
export const OFFLINE_RIFT_THRESHOLD_SECONDS = 60 * 60;
export const ARTIFACT_BASE_CHANCE = 0.12;
export const ARTIFACT_CHANCE_PER_MISS = 0.04;
export const ARTIFACT_PITY_RIFTS = 12;

export interface ActivityActionResult {
  ok: boolean;
  state: GameState;
  reason?: string;
  reward?: RewardToken;
  artifactId?: string;
  duplicateArtifactId?: string;
  gainedArtifactDust?: number;
  gainedGold?: number;
  gainedResources?: Record<string, number>;
  addedBoostSeconds?: number;
}

const SKILL_GROUPS: readonly (readonly SkillId[])[] = [
  ['Attack', 'Strength', 'Defence', 'Constitution', 'Ranged', 'Magic', 'Prayer', 'Slayer'],
  ['Woodcutting', 'Mining', 'Fishing', 'Farming', 'Hunter', 'Archaeology', 'Divination'],
  ['Smithing', 'Cooking', 'Firemaking', 'Crafting', 'Fletching', 'Herblore', 'Construction', 'Runecrafting'],
  ['Agility', 'Thieving', 'Dungeoneering', 'Invention', 'Summoning']
];

function deterministicUnit(seed: number): number {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43_758.5453;
  return value - Math.floor(value);
}

function riftDelayMs(sequence: number): number {
  return (4 + deterministicUnit(sequence + 71) * 5) * 60_000;
}

export function contractMetricValue(state: GameState, metric: ContractMetric, objective?: Pick<ContractObjective, 'skill' | 'resource'>): number {
  switch (metric) {
    case 'manualActions': return state.stats.manualActions ?? 0;
    case 'skillSwitches': return state.stats.skillSwitches ?? 0;
    case 'resourceUnits': return objective?.resource ? state.stats.resourceUnitsEarned?.[objective.resource] ?? 0 : state.stats.totalResourceUnitsEarned ?? 0;
    case 'resourcesSold': return objective?.resource ? state.stats.resourcesSoldByResource?.[objective.resource] ?? 0 : state.stats.resourcesSold ?? 0;
    case 'toolsPurchased': return objective?.skill ? state.stats.toolsPurchasedBySkill?.[objective.skill] ?? 0 : state.stats.toolsPurchased ?? 0;
    case 'hallsPurchased': return objective?.skill ? state.stats.hallsPurchasedBySkill?.[objective.skill] ?? 0 : state.stats.hallsPurchased ?? 0;
    case 'skillLevels': return objective?.skill ? state.stats.skillLevelsGainedBySkill?.[objective.skill] ?? 0 : state.stats.skillLevelsGained ?? 0;
    case 'skillXp': return objective?.skill ? state.stats.skillXpEarned?.[objective.skill] ?? 0 : Object.values(state.stats.skillXpEarned ?? {}).reduce((sum, value) => sum + (value ?? 0), 0);
    case 'activeSkillSeconds': return objective?.skill ? state.stats.activeSecondsBySkill?.[objective.skill] ?? 0 : state.stats.activeSeconds ?? 0;
    case 'criticalProcs': return state.stats.criticalTrainingProcs ?? 0;
    case 'surgeSeconds': return state.stats.surgeTrainingSeconds ?? 0;
    case 'riftsOpened': return state.prestige.activities?.riftsOpened ?? 0;
  }
}

function scaled(base: number, growth: number, tier: number, step = 1): number {
  return Math.max(step, Math.round((base * growth ** (tier - 1)) / step) * step);
}

function displayName(value: string): string {
  return value.replace(/(^|_)([a-z])/g, (_, prefix: string, letter: string) => `${prefix ? ' ' : ''}${letter.toUpperCase()}`);
}

function chooseSkills(state: GameState, sequence: number, count: number, pool: readonly SkillId[] = SKILLS): SkillId[] {
  const ordered = [...pool].sort((left, right) => {
    const levelDifference = state.run.skills[left].level - state.run.skills[right].level;
    if (levelDifference !== 0) return levelDifference;
    return ((SKILLS.indexOf(left) - sequence * 7 + SKILLS.length * 10) % SKILLS.length)
      - ((SKILLS.indexOf(right) - sequence * 7 + SKILLS.length * 10) % SKILLS.length);
  });
  return ordered.slice(0, Math.min(count, ordered.length));
}

function objective(
  state: GameState,
  id: string,
  metric: ContractMetric,
  label: string,
  target: number,
  filter: { skill?: SkillId; resource?: string } = {}
): ContractObjective {
  return { id, metric, label, target, baseline: contractMetricValue(state, metric, filter), ...filter };
}

function skillProgressObjective(state: GameState, id: string, skill: SkillId, xpTarget: number, resourceTarget: number): ContractObjective {
  if (state.run.skills[skill].level < 200) {
    return objective(state, id, 'skillXp', `Earn ${Math.round(xpTarget).toLocaleString('en-US')} ${skill} XP`, xpTarget, { skill });
  }
  const resource = RESOURCE_BY_SKILL[skill];
  return objective(state, id, 'resourceUnits', `Produce ${Math.round(resourceTarget).toLocaleString('en-US')} ${displayName(resource)}`, resourceTarget, { resource });
}

function activeContract(state: GameState, sequence: number, cycle: number, tier: number): { title: string; description: string; objectives: ContractObjective[] } {
  const variant = cycle % 5;
  if (variant === 0) {
    const target = scaled(50, 1.25, tier);
    return { title: 'Hands of the Realm', description: 'Turn active attention into immediate progress.', objectives: [objective(state, 'manual', 'manualActions', `Use Train now ${target} times`, target)] };
  }
  if (variant === 1) {
    const skills = chooseSkills(state, sequence, 3);
    const xp = scaled(2_500, 1.7, tier, 100);
    const resources = scaled(2_000, 1.7, tier, 100);
    return { title: 'Realm Rotation', description: 'Make meaningful progress along three different paths.', objectives: skills.map((skill) => skillProgressObjective(state, `rotation-${skill}`, skill, xp, resources)) };
  }
  if (variant === 2) {
    const [skill] = chooseSkills(state, sequence, 1);
    const seconds = scaled(300, 1.25, tier, 30);
    return { title: 'Focused Training', description: `Spend active training time on ${skill}; switching away pauses this objective.`, objectives: [objective(state, `focus-${skill}`, 'activeSkillSeconds', `Actively train ${skill} for ${Math.round(seconds / 60)} minutes`, seconds, { skill })] };
  }
  if (variant === 3) {
    const target = scaled(3, 1.25, tier);
    return { title: 'Critical Study', description: 'Wait for genuine Critical Training moments.', objectives: [objective(state, 'criticals', 'criticalProcs', `Trigger ${target} Critical Training moments`, target)] };
  }
  const seconds = scaled(180, 1.2, tier, 30);
  return { title: 'Surge Rider', description: 'Choose the right moment to activate a stored Production Surge.', objectives: [objective(state, 'surge', 'surgeSeconds', `Train for ${Math.round(seconds / 60)} minutes during a Surge`, seconds)] };
}

function economyContract(state: GameState, sequence: number, cycle: number, tier: number): { title: string; description: string; objectives: ContractObjective[] } {
  const variant = cycle % 5;
  const [hallSkill] = chooseSkills(state, sequence, 1);
  if (variant === 0) {
    const amount = scaled(2_000, 1.85, tier, 100);
    const resources = BUILDING_SUPPORT_RESOURCES_BY_SKILL[hallSkill];
    return {
      title: 'Supply Circuit',
      description: `Prepare both supply routes used by the ${hallSkill} Hall.`,
      objectives: resources.map((resource) => objective(state, `supply-${resource}`, 'resourceUnits', `Produce ${amount.toLocaleString('en-US')} ${displayName(resource)}`, amount, { resource }))
    };
  }
  if (variant === 1) {
    const targets = Math.min(3, 1 + Math.floor((tier - 1) / 4));
    const objectives: ContractObjective[] = [];
    if (state.run.skills[hallSkill].toolTier < 40) objectives.push(objective(state, `tool-${hallSkill}`, 'toolsPurchased', `Forge ${targets} ${hallSkill} Tool upgrade${targets === 1 ? '' : 's'}`, Math.min(targets, 40 - state.run.skills[hallSkill].toolTier), { skill: hallSkill }));
    if (state.run.skills[hallSkill].buildingLevel < 40) objectives.push(objective(state, `hall-${hallSkill}`, 'hallsPurchased', `Construct ${targets} ${hallSkill} Hall upgrade${targets === 1 ? '' : 's'}`, Math.min(targets, 40 - state.run.skills[hallSkill].buildingLevel), { skill: hallSkill }));
    if (objectives.length === 0) return economyContract(state, sequence, cycle + 1, tier);
    return { title: "Master's Commission", description: `Improve the equipment and infrastructure of ${hallSkill}.`, objectives };
  }
  if (variant === 2) {
    const skills = chooseSkills(state, sequence, 3);
    const amount = scaled(1_500, 1.8, tier, 100);
    return { title: 'Profitable Route', description: 'Trade meaningful quantities from three separate industries.', objectives: skills.map((skill) => {
      const resource = RESOURCE_BY_SKILL[skill];
      return objective(state, `sell-${resource}`, 'resourcesSold', `Sell ${amount.toLocaleString('en-US')} ${displayName(resource)}`, amount, { resource });
    }) };
  }
  const upgradeMetric: ContractMetric = variant === 3 ? 'hallsPurchased' : 'toolsPurchased';
  const eligible = SKILLS.filter((skill) => variant === 3 ? state.run.skills[skill].buildingLevel < 40 : state.run.skills[skill].toolTier < 40);
  if (eligible.length === 0) return economyContract(state, sequence, 0, tier);
  const skills = chooseSkills(state, sequence, Math.min(3, eligible.length), eligible);
  return {
    title: variant === 3 ? 'Realm Reconstruction' : 'Masterwork Order',
    description: `Spread upgrades across ${skills.length} different skill paths.`,
    objectives: skills.map((skill) => objective(state, `${upgradeMetric}-${skill}`, upgradeMetric, `${variant === 3 ? 'Construct a Hall' : 'Forge a Tool'} upgrade for ${skill}`, 1, { skill }))
  };
}

function journeyContract(state: GameState, sequence: number, cycle: number, tier: number): { title: string; description: string; objectives: ContractObjective[] } {
  const variant = cycle % 5;
  if (variant === 0) {
    const amount = scaled(10_000, 2, tier, 500);
    return { title: 'Realm Harvest', description: 'Let every productive path contribute to one larger order.', objectives: [objective(state, 'harvest', 'resourceUnits', `Produce ${amount.toLocaleString('en-US')} resources`, amount)] };
  }
  if (variant === 1 || variant === 3) {
    const skills = chooseSkills(state, sequence, 3, SKILLS.filter((skill) => state.run.skills[skill].level < 200));
    if (skills.length === 0) return journeyContract(state, sequence, 0, tier);
    const requested = scaled(variant === 1 ? 3 : 4, 1.25, tier);
    return {
      title: variant === 1 ? 'Circle of Masters' : 'Raise the Weak',
      description: variant === 1 ? 'Develop several disciplines instead of relying on one.' : 'Strengthen paths currently lagging behind the rest of your realm.',
      objectives: skills.map((skill) => {
        const levels = Math.min(requested, 200 - state.run.skills[skill].level);
        return objective(state, `levels-${skill}`, 'skillLevels', `Gain ${levels} ${skill} level${levels === 1 ? '' : 's'}`, levels, { skill });
      })
    };
  }
  if (variant === 2) {
    const xp = scaled(4_000, 1.65, tier, 100);
    const resources = scaled(3_000, 1.65, tier, 100);
    const skills = SKILL_GROUPS.map((group, index) => chooseSkills(state, sequence + index * 3, 1, group)[0]);
    return { title: 'Crossroads', description: 'Advance one path from every corner of the Skill Codex.', objectives: skills.map((skill) => skillProgressObjective(state, `crossroads-${skill}`, skill, xp, resources)) };
  }
  const rifts = scaled(1, 1.2, tier);
  return { title: 'Rift Warden', description: 'Stay alert for rare Realm Rifts; Dormant Rifts count too.', objectives: [objective(state, 'rifts', 'riftsOpened', `Open ${rifts} Realm Rift${rifts === 1 ? '' : 's'}`, rifts)] };
}

function makeContractReward(sequence: number, tier: number): RewardToken {
  const kinds: RewardKind[] = ['goldCache', 'supplyCache', 'productionSurge'];
  const kind = kinds[sequence % kinds.length];
  const amount = kind === 'goldCache'
    ? Math.round(150 * 2.15 ** (tier - 1))
    : kind === 'supplyCache'
      ? Math.round(120 * 2.05 ** (tier - 1))
      : 900 + (tier - 1) * 180;
  return { id: `contract-reward-${sequence}`, kind, tier, amount };
}

function makeRiftReward(sequence: number, tier: number): RewardToken {
  const kinds: RewardKind[] = ['goldCache', 'supplyCache', 'productionSurge'];
  const kind = kinds[sequence % kinds.length];
  const amount = kind === 'goldCache'
    ? Math.round(50 * 2 ** (tier - 1))
    : kind === 'supplyCache'
      ? Math.round(40 * 1.9 ** (tier - 1))
      : 180 + (tier - 1) * 60;
  return { id: `rift-reward-${sequence}`, kind, tier, amount };
}

function makeContract(state: GameState, sequence: number, forcedCategory?: ContractCategory): RealmContract {
  const tier = Math.min(10, 1 + Math.floor((state.prestige.ascensions + Math.floor(sequence / CONTRACT_SLOTS)) / 3));
  const category: ContractCategory = forcedCategory ?? (sequence % CONTRACT_SLOTS === 0 ? 'active' : sequence % CONTRACT_SLOTS === 1 ? 'economy' : 'journey');
  const cycle = Math.floor(sequence / CONTRACT_SLOTS);
  const definition = category === 'active'
    ? activeContract(state, sequence, cycle, tier)
    : category === 'economy'
      ? economyContract(state, sequence, cycle, tier)
      : journeyContract(state, sequence, cycle, tier);
  const [first] = definition.objectives;
  const compound = definition.objectives.length > 1;
  return {
    id: `contract-${sequence}`,
    metric: first.metric,
    title: definition.title,
    description: definition.description,
    target: compound ? 100 : first.target,
    baseline: compound ? 0 : first.baseline,
    tier,
    reward: makeContractReward(sequence, tier),
    completed: false,
    category,
    objectives: definition.objectives
  };
}

export function createInitialActivities(now = Date.now()): RealmActivityState {
  return {
    contractSequence: 0,
    contracts: [],
    rewardVault: [],
    productionBoostSeconds: 0,
    nextRiftAt: now + riftDelayMs(0),
    dormantRift: false,
    riftsOpened: 0,
    artifacts: [],
    artifactMisses: 0,
    artifactDust: 0
  };
}

export function activitiesFor(state: GameState): RealmActivityState {
  const current = state.prestige.activities ?? createInitialActivities(state.updatedAt);
  const contracts = (current.contracts ?? []).map((contract, index) => {
    if (contract.metric !== 'skillSwitches' || contract.completed) return contract;
    const parsedSequence = Number(contract.id.replace('contract-', ''));
    const replacement = makeContract(state, Number.isFinite(parsedSequence) ? parsedSequence : index);
    return { ...replacement, id: contract.id, reward: contract.reward };
  });
  let sequence = Math.max(current.contractSequence ?? 0, contracts.length);
  while (contracts.length < CONTRACT_SLOTS) contracts.push(makeContract(state, sequence++));
  return {
    contractSequence: sequence,
    contracts,
    rewardVault: current.rewardVault ?? [],
    productionBoostSeconds: current.productionBoostSeconds ?? 0,
    nextRiftAt: current.nextRiftAt ?? state.updatedAt + riftDelayMs(current.riftsOpened ?? 0),
    activeRift: current.activeRift,
    dormantRift: current.dormantRift ?? false,
    riftsOpened: current.riftsOpened ?? 0,
    artifacts: current.artifacts ?? [],
    artifactMisses: current.artifactMisses ?? 0,
    artifactDust: current.artifactDust ?? 0
  };
}

export function contractObjectives(contract: RealmContract): ContractObjective[] {
  return contract.objectives?.length ? contract.objectives : [{
    id: 'legacy',
    metric: contract.metric,
    label: contract.description,
    target: contract.target,
    baseline: contract.baseline
  }];
}

export function contractObjectiveProgress(state: GameState, objective: ContractObjective): number {
  return Math.min(objective.target, Math.max(0, contractMetricValue(state, objective.metric, objective) - objective.baseline));
}

export function contractProgressRatio(state: GameState, contract: RealmContract): number {
  const objectives = contractObjectives(contract);
  return objectives.reduce((sum, item) => sum + contractObjectiveProgress(state, item) / item.target, 0) / objectives.length;
}

export function contractProgress(state: GameState, contract: RealmContract): number {
  const objectives = contractObjectives(contract);
  if (objectives.length === 1) return contractObjectiveProgress(state, objectives[0]);
  return contractProgressRatio(state, contract) * 100;
}

export function recordContractProgress(state: GameState): GameState {
  const activities = activitiesFor(state);
  const contracts = activities.contracts.map((contract) => ({
    ...contract,
    completed: contract.completed || contractProgress(state, contract) >= contract.target
  }));
  const stored = state.prestige.activities;
  const unchanged = stored
    && stored.contractSequence === activities.contractSequence
    && stored.contracts?.length === contracts.length
    && contracts.every((contract, index) => contract.completed === stored.contracts[index]?.completed
      && Boolean(contract.objectives?.length) === Boolean(stored.contracts[index]?.objectives?.length));
  if (unchanged) return state;
  return { ...state, prestige: { ...state.prestige, activities: { ...activities, contracts } } };
}

export function claimContractReward(state: GameState, contractId: string, now = Date.now()): ActivityActionResult {
  const activities = activitiesFor(state);
  const contract = activities.contracts.find((item) => item.id === contractId);
  if (!contract) return { ok: false, state, reason: 'Contract not found.' };
  if (!contract.completed && contractProgress(state, contract) < contract.target) return { ok: false, state, reason: 'Contract is not complete yet.' };
  const replacement = makeContract(state, activities.contractSequence, contract.category);
  const contracts = activities.contracts.map((item) => item.id === contractId ? replacement : item);
  return {
    ok: true,
    reward: contract.reward,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: {
        ...state.prestige,
        activities: {
          ...activities,
          contractSequence: activities.contractSequence + 1,
          contracts,
          rewardVault: [...activities.rewardVault, contract.reward]
        }
      }
    }
  };
}

export function rewardDescription(reward: RewardToken): string {
  if (reward.kind === 'goldCache') return `${reward.amount.toLocaleString('en-US')} gold`;
  if (reward.kind === 'supplyCache') return `${reward.amount.toLocaleString('en-US')} Hall materials`;
  return `${Math.round(reward.amount / 60)} minutes of ×${PRODUCTION_SURGE_MULTIPLIER} production and XP`;
}

export function useReward(state: GameState, rewardId: string, now = Date.now()): ActivityActionResult {
  const activities = activitiesFor(state);
  const reward = activities.rewardVault.find((item) => item.id === rewardId);
  if (!reward) return { ok: false, state, reason: 'Reward not found.' };
  const rewardVault = activities.rewardVault.filter((item) => item.id !== rewardId);
  let run = state.run;
  let nextActivities = { ...activities, rewardVault };
  let gainedGold: number | undefined;
  let gainedResources: Record<string, number> | undefined;
  let addedBoostSeconds: number | undefined;
  if (reward.kind === 'goldCache') {
    run = { ...run, gold: run.gold + reward.amount };
    gainedGold = reward.amount;
  } else if (reward.kind === 'supplyCache') {
    const support = BUILDING_SUPPORT_RESOURCES_BY_SKILL[run.activeSkill];
    const each = reward.amount / support.length;
    const resources = { ...run.resources };
    gainedResources = {};
    for (const resource of support) {
      resources[resource] = (resources[resource] ?? 0) + each;
      gainedResources[resource] = each;
    }
    run = { ...run, resources };
  } else {
    addedBoostSeconds = reward.amount * constellationSurgeDurationMultiplier(state);
    nextActivities.productionBoostSeconds += addedBoostSeconds;
  }
  return {
    ok: true,
    reward,
    gainedGold,
    gainedResources,
    addedBoostSeconds,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      run,
      prestige: { ...state.prestige, activities: nextActivities }
    }
  };
}

export function advanceRealmEvents(state: GameState, now: number, mode: 'active' | 'offline', elapsedSeconds: number): GameState {
  let activities = activitiesFor(state);
  if (mode === 'offline') {
    const activeRift = activities.activeRift && activities.activeRift.expiresAt >= now ? activities.activeRift : undefined;
    activities = {
      ...activities,
      activeRift,
      dormantRift: activities.dormantRift || elapsedSeconds >= OFFLINE_RIFT_THRESHOLD_SECONDS,
      nextRiftAt: elapsedSeconds >= OFFLINE_RIFT_THRESHOLD_SECONDS ? now + riftDelayMs(activities.riftsOpened + 1) : activities.nextRiftAt
    };
  } else {
    let activeRift = activities.activeRift;
    let nextRiftAt = activities.nextRiftAt;
    if (activeRift && now > activeRift.expiresAt) {
      activeRift = undefined;
      nextRiftAt = now + riftDelayMs(activities.riftsOpened + 3);
    }
    if (!activeRift && now >= nextRiftAt) {
      const spawnedAt = now;
      activeRift = { id: `rift-${activities.riftsOpened}-${spawnedAt}`, spawnedAt, expiresAt: spawnedAt + RIFT_LIFETIME_MS };
    }
    activities = { ...activities, activeRift, nextRiftAt };
  }
  return { ...state, prestige: { ...state.prestige, activities } };
}

export function openRealmRift(state: GameState, now = Date.now(), random: () => number = Math.random): ActivityActionResult {
  const activities = activitiesFor(state);
  const active = activities.activeRift && activities.activeRift.expiresAt >= now;
  if (!active && !activities.dormantRift) return { ok: false, state, reason: 'The Rift has already faded.' };
  const sequence = activities.riftsOpened + 1;
  const tier = Math.min(10, 1 + Math.floor(state.prestige.ascensions / 3));
  const reward = makeRiftReward(sequence, tier);
  const misses = activities.artifactMisses ?? 0;
  const guaranteed = misses >= ARTIFACT_PITY_RIFTS;
  const dropChance = Math.min(0.6, ARTIFACT_BASE_CHANCE + misses * ARTIFACT_CHANCE_PER_MISS);
  const artifactDropped = guaranteed || random() < dropChance;
  const pool = ARTIFACTS;
  const totalWeight = pool.reduce((sum, artifact) => sum + ARTIFACT_RARITY_WEIGHTS[artifactRarity(artifact)], 0);
  let artifact = artifactDropped && pool.length > 0 ? pool[pool.length - 1] : undefined;
  let artifactRoll = artifactDropped ? random() * totalWeight : 0;
  if (artifactDropped) {
    for (const candidate of pool) {
      artifactRoll -= ARTIFACT_RARITY_WEIGHTS[artifactRarity(candidate)];
      if (artifactRoll <= 0) { artifact = candidate; break; }
    }
  }
  const duplicateArtifactId = artifact && activities.artifacts.includes(artifact.id) ? artifact.id : undefined;
  const artifactId = artifact && !duplicateArtifactId ? artifact.id : undefined;
  const gainedArtifactDust = duplicateArtifactId && artifact ? artifactDuplicateDust(artifact) : 0;
  const artifacts = artifactId ? [...activities.artifacts, artifactId] : activities.artifacts;
  return {
    ok: true,
    reward,
    artifactId,
    duplicateArtifactId,
    gainedArtifactDust,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: {
        ...state.prestige,
        activities: {
          ...activities,
          activeRift: undefined,
          dormantRift: false,
          nextRiftAt: now + riftDelayMs(sequence),
          riftsOpened: sequence,
          artifacts,
          artifactMisses: artifact ? 0 : misses + 1,
          artifactDust: (activities.artifactDust ?? 0) + gainedArtifactDust,
          rewardVault: [...activities.rewardVault, reward]
        }
      }
    }
  };
}

export function craftArtifact(state: GameState, artifactId: string, now = Date.now()): ActivityActionResult {
  const activities = activitiesFor(state);
  const artifact = ARTIFACTS.find((candidate) => candidate.id === artifactId);
  if (!artifact) return { ok: false, state, reason: 'Artifact not found.' };
  if (activities.artifacts.includes(artifactId)) return { ok: false, state, reason: 'This Artifact is already in your Codex.' };
  const cost = artifactDustCost(artifact);
  if ((activities.artifactDust ?? 0) < cost) return { ok: false, state, reason: `You need ${cost} Artifact Dust.` };
  return {
    ok: true,
    artifactId,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      prestige: {
        ...state.prestige,
        activities: {
          ...activities,
          artifacts: [...activities.artifacts, artifactId],
          artifactDust: (activities.artifactDust ?? 0) - cost,
          artifactMisses: 0
        }
      }
    }
  };
}

export function currentProductionSurgeSeconds(state: GameState): number {
  return activitiesFor(state).productionBoostSeconds;
}
